import { Link } from "react-router-dom";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BUSINESS } from "@/lib/constants";

export default function TermsConditions() {
  usePageMeta(
    "Terms & Conditions | J11 Consulting LLC",
    "Terms & Conditions for the J11 Consulting LLC website, including SMS messaging terms, educational nature of services, and limitation of liability."
  );

  return (
    <LegalLayout
      title="Terms & Conditions"
      subtitle={`Last updated: July 2026. These Terms & Conditions govern your use of the ${BUSINESS.name} website and services.`}
      testId="terms-page"
    >
      <LegalSection id="terms-acceptance" title="1. Acceptance of Terms">
        <p>
          By accessing or using this website, you agree to these Terms &amp; Conditions. This website
          is operated by {BUSINESS.name}. If you do not agree with these terms, please do not use this
          website.
        </p>
      </LegalSection>

      <LegalSection id="terms-age-requirement" title="2. Age Requirement">
        <p>
          You must be at least 18 years old to use {BUSINESS.name} services, submit forms,
          participate in our programs, or consent to SMS communications.
        </p>
        <p>
          By using this website or submitting information to {BUSINESS.name}, you represent that you
          are 18 years of age or older.
        </p>
        <p>
          If you are under 18, do not submit personal information or enroll in {BUSINESS.shortName}{" "}
          services.
        </p>
      </LegalSection>

      <LegalSection id="terms-educational-nature" title="3. Educational Nature of Services">
        <p>
          {BUSINESS.name} provides credit education, financial literacy coaching, mentorship, and
          funding readiness education. The content on this website and the services we provide are
          educational in nature and do not constitute legal, tax, investment, or individualized
          financial advice. {BUSINESS.shortName} is not a bank, lender, financial institution, credit
          bureau, or law firm.
        </p>
      </LegalSection>

      <LegalSection id="terms-no-guarantees" title="4. No Financial Outcome Guarantees">
        <p className="font-semibold text-[#F2EEE3]">
          {BUSINESS.name} does not guarantee any specific credit score, credit report outcome, funding
          approval, loan amount, financing terms or financial result.
        </p>
        <p>
          Credit reporting, credit scores, lending requirements, and financing decisions are
          controlled by third parties. Individual results vary based on each person's unique
          circumstances.
        </p>
      </LegalSection>

      <LegalSection id="terms-no-lender-affiliation" title="5. No Lender Affiliation">
        <p>
          {BUSINESS.shortName} does not make lending decisions and is not affiliated with, endorsed
          by, or acting on behalf of any lender or financial institution unless specifically disclosed
          in writing. We cannot control the decisions of lenders, credit bureaus, or other external
          organizations.
        </p>
      </LegalSection>

      <LegalSection id="terms-user-responsibilities" title="6. User Responsibilities">
        <p>
          When using this website, you agree to provide accurate information through our forms and to
          use the website only for lawful purposes. You are responsible for independently evaluating
          financial products and for consulting appropriately licensed professionals when necessary.
        </p>
      </LegalSection>

      <LegalSection id="terms-intellectual-property" title="7. Intellectual Property">
        <p>
          All content on this website, including text, design, graphics, and logos, is the property of{" "}
          {BUSINESS.name} or its licensors and is protected by applicable intellectual property laws.
          You may not reproduce or distribute website content without our prior written permission.
        </p>
      </LegalSection>

      <LegalSection id="terms-website-use" title="8. Website Use">
        <p>
          You may not attempt to interfere with the operation or security of this website, scrape or
          harvest information from it, or use it in any way that violates applicable law.
        </p>
      </LegalSection>

      <LegalSection id="terms-third-party" title="9. Third-Party Services">
        <p>
          Our website may use third-party service providers for hosting, communications, and
          operational purposes. We are not responsible for the content, policies, or practices of
          third-party websites or services that may be linked from or used in connection with our
          website.
        </p>
      </LegalSection>

      <LegalSection id="terms-appointments" title="10. Appointment & Consultation Communications">
        <p>
          When you request a consultation, we may contact you by phone, email, or — only if you have
          opted in — SMS, regarding your inquiry, consultation scheduling, appointment reminders,
          requested educational information, and follow-up related to our educational and mentorship
          programs.
        </p>
      </LegalSection>

      <LegalSection id="terms-sms" title="11. SMS Messaging Terms">
        <p>
          By voluntarily opting into SMS messaging from {BUSINESS.name}, users may receive messages
          related to:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          <li>Consultation confirmations</li>
          <li>Appointment reminders</li>
          <li>Requested follow-up</li>
          <li>Educational resources</li>
          <li>Client support</li>
          <li>Account-related communications</li>
        </ul>
        <p>Message frequency varies. Message and data rates may apply.</p>
        <p>Reply STOP to unsubscribe. Reply HELP for assistance or call {BUSINESS.phone}.</p>
        <p>Consent to SMS messages is not a condition of purchase.</p>
        <p>Carriers are not liable for delayed or undelivered messages.</p>
        <p>
          {BUSINESS.shortName}'s handling of mobile information is governed by its{" "}
          <Link
            to="/privacy-policy"
            data-testid="terms-privacy-policy-link"
            className="font-medium text-[#D8B54A] underline underline-offset-2 hover:text-[#E4C463]"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </LegalSection>

      <LegalSection id="terms-liability" title="12. Limitation of Liability">
        <p>
          To the fullest extent permitted by law, {BUSINESS.name} will not be liable for any indirect,
          incidental, consequential, or punitive damages arising out of or related to your use of this
          website or our educational services. This website and its content are provided on an "as is"
          and "as available" basis without warranties of any kind.
        </p>
      </LegalSection>

      <LegalSection id="terms-governing-law" title="13. Governing Law">
        <p>
          These Terms &amp; Conditions are governed by the laws of the State of Florida, without
          regard to conflict-of-law principles.
        </p>
      </LegalSection>

      <LegalSection id="terms-changes" title="14. Updates to Terms">
        <p>
          We may update these Terms &amp; Conditions from time to time. Any changes will be posted on
          this page with an updated revision date. Your continued use of the website after changes are
          posted constitutes acceptance of the updated terms.
        </p>
      </LegalSection>

      <LegalSection id="terms-contact" title="15. Contact Information">
        <p>If you have questions about these Terms &amp; Conditions, contact us at:</p>
        <p className="font-semibold text-[#F2EEE3]">
          {BUSINESS.name}
          <br />
          195 Malabar Rd, Suite #3
          <br />
          Palm Bay, FL 32907
          <br />
          Phone: {BUSINESS.phone}
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
